import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Card, EmptyState, Icon, Input, Modal, Spinner, StatusPill } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { brl, datePt, statusLabel } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface ApptLite {
  id: string;
  customer_id: string;
  scheduled_date: string;
  start_time: string;
  service_name: string;
  price_cents: number;
  status: string;
  customerName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  professionalName: string;
}

interface ClientRecord {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  appointments: ApptLite[];
  total: number;
  completed: number;
  upcoming: number;
  cancelled: number;
  noShow: number;
  spentCents: number;
  lastDate: string | null;
}

const STATUSES = ['pending', 'confirmed', 'completed', 'cancelled_by_customer', 'cancelled_by_professional', 'no_show'] as const;

const TONE: Record<string, 'neutral' | 'positive' | 'warning' | 'danger'> = {
  pending: 'warning',
  confirmed: 'positive',
  completed: 'neutral',
  cancelled_by_customer: 'danger',
  cancelled_by_professional: 'danger',
  no_show: 'danger'
};

export function PanelClientsPage(): JSX.Element {
  usePageMeta(tr('Clientes'), tr('Base de clientes derivada dos agendamentos registrados no estabelecimento.'));
  const { active } = useAuth();
  const businessId = active?.businessId ?? '';

  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [apptCount, setApptCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ClientRecord | null>(null);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError(null);
    try {
      const responses = await Promise.all(
        STATUSES.map((status) =>
          api.get<{ appointments: ApptLite[] }>(`/api/appointments/${businessId}/appointments?status=${status}`)
        )
      );
      const all = responses.flatMap((r) => r.appointments);
      setApptCount(all.length);
      const map = new Map<string, ClientRecord>();
      for (const a of all) {
        let rec = map.get(a.customer_id);
        if (!rec) {
          rec = {
            id: a.customer_id,
            name: a.customerName,
            phone: a.customerPhone,
            email: a.customerEmail,
            appointments: [],
            total: 0,
            completed: 0,
            upcoming: 0,
            cancelled: 0,
            noShow: 0,
            spentCents: 0,
            lastDate: null
          };
          map.set(a.customer_id, rec);
        }
        rec.appointments.push(a);
        rec.total += 1;
        if (a.status === 'completed') {
          rec.completed += 1;
          rec.spentCents += a.price_cents;
        } else if (a.status === 'pending' || a.status === 'confirmed') {
          rec.upcoming += 1;
        } else if (a.status === 'cancelled_by_customer' || a.status === 'cancelled_by_professional') {
          rec.cancelled += 1;
        } else if (a.status === 'no_show') {
          rec.noShow += 1;
        }
        if (!rec.lastDate || a.scheduled_date > rec.lastDate) rec.lastDate = a.scheduled_date;
        if (!rec.phone && a.customerPhone) rec.phone = a.customerPhone;
        if (!rec.email && a.customerEmail) rec.email = a.customerEmail;
      }
      const list = [...map.values()].sort((x, y) => (y.lastDate ?? '').localeCompare(x.lastDate ?? ''));
      for (const c of list) {
        c.appointments.sort((x, y) => y.scheduled_date.localeCompare(x.scheduled_date) || y.start_time.localeCompare(x.start_time));
      }
      setClients(list);
    } catch (err) {
      setError(err);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.phone ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q)
    );
  }, [clients, search]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{tr("Clientes")}</h1>
          <p className="nl-hint">
            {loading ? tr('Construindo a base a partir dos atendimentos…') : tr('{clients} cliente(s) identificados em {appointments} atendimento(s)', { clients: clients.length, appointments: apptCount })}
          </p>
        </div>
        <label className="relative w-full max-w-xs">
          <span className="sr-only">{tr("Buscar cliente")}</span>
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted">
            <Icon name="search" size={16} />
          </span>
          <Input className="pl-9" placeholder={tr("Nome, telefone ou e-mail")} value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>

      {loading ? (
        <Card>
          <Spinner label={tr("Carregando atendimentos…")} />
        </Card>
      ) : null}

      {!loading && error ? <ClientsError error={error} /> : null}

      {!loading && !error ? (
        <Card as="section" className="overflow-x-auto">
          {filtered.length === 0 ? (
            <EmptyState
              title={clients.length === 0 ? tr('Nenhum atendimento registrado ainda') : tr('Nenhum cliente corresponde à busca')}
              description={
                clients.length === 0
                  ? tr('A base de clientes aparece conforme os agendamentos são criados (online ou pelo painel).')
                  : tr('Ajuste o termo de busca.')
              }
            />
          ) : (
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-line text-muted">
                  <th className="py-2 pr-3 font-semibold">{tr("Cliente")}</th>
                  <th className="py-2 pr-3 font-semibold">{tr("Contato")}</th>
                  <th className="py-2 pr-3 font-semibold">{tr("Atendimentos")}</th>
                  <th className="py-2 pr-3 font-semibold">{tr("Futuros")}</th>
                  <th className="py-2 pr-3 font-semibold">{tr("Faltas / cancel.")}</th>
                  <th className="py-2 pr-3 font-semibold">{tr("Total concluído")}</th>
                  <th className="py-2 font-semibold">{tr("Última visita")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface" onClick={() => setSelected(c)}>
                    <td className="py-2.5 pr-3 font-semibold">{c.name}</td>
                    <td className="py-2.5 pr-3 text-muted">
                      {c.phone ?? '—'}
                      {c.email ? ` · ${c.email}` : ''}
                    </td>
                    <td className="py-2.5 pr-3">{c.total}</td>
                    <td className="py-2.5 pr-3">{c.upcoming}</td>
                    <td className="py-2.5 pr-3">
                      {c.noShow} / {c.cancelled}
                    </td>
                    <td className="py-2.5 pr-3">{brl(c.spentCents)}</td>
                    <td className="py-2.5">{datePt(c.lastDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ) : null}

      <Modal open={selected !== null} onClose={() => setSelected(null)} title={selected ? tr('Atendimentos de {name}', { name: selected.name }) : tr('Cliente')}>
        {selected ? (
          <div className="space-y-3">
            <p className="nl-hint text-sm">
              {selected.phone ? tr('Telefone: {phone}', { phone: selected.phone }) : tr('Sem telefone registrado')}
              {selected.email ? ` · E-mail: ${selected.email}` : ''}
            </p>
            <ul className="divide-y divide-line">
              {selected.appointments.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <div className="min-w-0">
                    <p className="font-semibold">
                      {datePt(a.scheduled_date)} {a.start_time} · {a.service_name}
                    </p>
                    <p className="nl-hint truncate">
                      {a.professionalName} · {brl(a.price_cents)}
                    </p>
                  </div>
                  <StatusPill tone={TONE[a.status] ?? 'neutral'}>{statusLabel(a.status)}</StatusPill>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function ClientsError({ error }: { error: unknown }): JSX.Element {
  if (error instanceof ApiError && error.status === 402) {
    return (
      <Alert tone="danger" title={tr("Assinatura necessária")}>
        {error.message}{' '}
        <Link className="font-semibold underline" to="/painel/plano">
          {tr("Ir para Meu plano")}
        </Link>
        .
      </Alert>
    );
  }
  if (error instanceof ApiError && error.status === 403) {
    return (
      <Alert tone="warning" title={tr("Permissão insuficiente")}>
        {tr("Seu perfil não tem acesso aos agendamentos que alimentam esta base de clientes.")} {error.message}
      </Alert>
    );
  }
  return <Alert tone="danger" title={tr("Erro ao carregar clientes")}>{error instanceof Error ? error.message : 'Tente novamente.'}</Alert>;
}
